import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '@/lib/api/client.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

type Report = {
  mostRequestedNotStocked: Array<{ partName: string; sku: string | null; requests: number; qty: number }>;
  averageHoursToReceive: number | null;
  receivedSamples: number;
  openByAge: { underOneDay: number; oneToThree: number; threeToSeven: number; overSeven: number };
  openCount: number;
  ticketsDelayed: Array<{ ticketId: string; ticketNumber: string; daysLate: number; openRequests: number }>;
  daysLost: number;
  cancelled: Array<{ reason: string; total: number }>;
};

export function PartsRequestReportPage() {
  const f = useReportFilters();
  const q = useQuery({
    queryKey: ['parts-request-report', f.period, f.storeId, f.dateFrom, f.dateTo],
    queryFn: () =>
      apiGet<Report>('/api/v1/reports/parts-requests', {
        params: {
          period: f.period,
          storeId: f.storeId || undefined,
          dateFrom: f.dateFrom || undefined,
          dateTo: f.dateTo || undefined,
        },
      }).then((r) => r.data),
  });

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading report…</div>;
  if (q.isError || !q.data) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load this report.</div>;
  const data = q.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Parts requests</h1>
        <p className="text-sm text-slate-500">What is being asked for, how long it takes, and which promises are waiting on parts.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border bg-white p-4">
          <p className="text-xs uppercase text-slate-500">Open now</p>
          <p className="text-2xl font-bold">{data.openCount}</p>
        </div>
        <div className="rounded-2xl border bg-white p-4">
          <p className="text-xs uppercase text-slate-500">Average request to received</p>
          <p className="text-2xl font-bold">{data.averageHoursToReceive == null ? '—' : `${data.averageHoursToReceive}h`}</p>
          <p className="text-xs text-slate-500">{data.receivedSamples} received in this period</p>
        </div>
        <div className="rounded-2xl border bg-white p-4">
          <p className="text-xs uppercase text-slate-500">Days lost past promise</p>
          <p className="text-2xl font-bold">{data.daysLost}</p>
        </div>
      </div>
      <section className="rounded-2xl border bg-white p-4">
        <h2 className="font-semibold">Most requested, not in stock</h2>
        <p className="text-sm text-slate-500">These also raise the reorder quantity on purchase-order suggestions.</p>
        <ul className="mt-3 divide-y text-sm">
          {data.mostRequestedNotStocked.map((row) => (
            <li key={`${row.partName}-${row.sku ?? ''}`} className="flex justify-between py-2">
              <span>{row.partName}{row.sku ? ` · ${row.sku}` : ''}</span>
              <span className="text-slate-600">{row.requests} requests · qty {row.qty}</span>
            </li>
          ))}
          {data.mostRequestedNotStocked.length === 0 ? <li className="py-2 text-slate-500">Nothing unstocked was requested in this period.</li> : null}
        </ul>
      </section>
      <section className="rounded-2xl border bg-white p-4">
        <h2 className="font-semibold">Open requests by age</h2>
        <ul className="mt-2 text-sm text-slate-700">
          <li>Under 1 day: {data.openByAge.underOneDay}</li>
          <li>1–3 days: {data.openByAge.oneToThree}</li>
          <li>3–7 days: {data.openByAge.threeToSeven}</li>
          <li>Over 7 days: {data.openByAge.overSeven}</li>
        </ul>
      </section>
      <section className="rounded-2xl border bg-white p-4">
        <h2 className="font-semibold">Tickets delayed by parts</h2>
        <ul className="mt-2 divide-y text-sm">
          {data.ticketsDelayed.map((row) => (
            <li key={row.ticketId} className="flex justify-between py-2">
              <Link className="font-semibold text-primary" to={`/tickets/${row.ticketId}`}>{row.ticketNumber}</Link>
              <span>{row.daysLate} days late · {row.openRequests} open</span>
            </li>
          ))}
          {data.ticketsDelayed.length === 0 ? <li className="py-2 text-slate-500">No promised tickets are waiting on parts.</li> : null}
        </ul>
      </section>
      <section className="rounded-2xl border bg-white p-4">
        <h2 className="font-semibold">Cancelled</h2>
        <ul className="mt-2 divide-y text-sm">
          {data.cancelled.map((row) => (
            <li key={row.reason} className="flex justify-between py-2">
              <span>{row.reason}</span>
              <span>{row.total}</span>
            </li>
          ))}
          {data.cancelled.length === 0 ? <li className="py-2 text-slate-500">No cancellations in this period.</li> : null}
        </ul>
      </section>
    </div>
  );
}
